import type { BillingRecord, CompanySubscription, IndustrySubscription } from '@/types';
import { simulate, uid } from '@/services/api/client';
import { withBackend } from '@/services/api/backend';
import { scopedCall } from '@/services/company/companyScope';
import { companyService } from '@/services/company/companyService';
import { PLANS } from '@/components/landing/pricingData';

// Real monthly price by plan name, sourced from the canonical PLANS catalog
// (the same one checkout and Stripe pricing derive from — see
// PLAN_MONTHLY_USD in firebase-functions-endeeko's recurring.service.ts) so
// this can't drift from what a company actually pays. A previous hardcoded
// table here (Starter: 49, Pro: 149, Business: 299, plus a nonexistent
// "Growth" plan) was showing every real plan's price wrong.
const monthlyFor = (planName?: string) =>
  PLANS.find((p) => p.name.toLowerCase() === (planName ?? '').toLowerCase())?.priceMonthly ?? 0;

const toCompanySubscription = (c: {
  id: string; name: string; logoUrl?: string; planName?: string; status?: string; tokens?: number; createdAt: string;
}): CompanySubscription => {
  const tokenAllowance = (c.tokens ?? 0) + 1500;
  return {
    id: `sub_${c.id}`,
    companyId: c.id,
    companyName: c.name,
    companyLogo: c.logoUrl,
    planId: (c.planName ?? 'starter').toLowerCase().replace(/\s+/g, '-'),
    planName: c.planName ?? 'Starter',
    status: c.status === 'inactive' ? 'expired' : ((c.status as CompanySubscription['status']) ?? 'active'),
    startDate: c.createdAt,
    autoRenew: c.status !== 'expired',
    tokenAllowance,
    tokensUsed: Math.max(0, tokenAllowance - (c.tokens ?? 0)),
    monthlyAmount: monthlyFor(c.planName),
    currency: 'USD',
  };
};

let CURRENT: IndustrySubscription | null = null;
const HISTORY: IndustrySubscription[] = [];
let BILLING: BillingRecord[] = [];

export const subscriptionService = {
  // Connected to endeeko `getBusinessSubscription` callable (company-scoped);
  // scopedCall injects the active company id and skips the backend when there's
  // no active company, so it never fires a companyId-less 400 at startup.
  getActive: () => scopedCall<IndustrySubscription | null>('getBusinessSubscription', {}, () => simulate(CURRENT)),
  // Connected to endeeko `getSubscriptionHistory` / `getBillingRecords` callables.
  getHistory: () => withBackend<IndustrySubscription[]>('getSubscriptionHistory', {}, () => simulate([...HISTORY])),
  getBilling: () => withBackend<BillingRecord[]>('getBillingRecords', {}, () => simulate([...BILLING])),

  /** One subscription per company the user owns. */
  getSubscriptionsForUser: async (userId: string): Promise<CompanySubscription[]> => {
    const companies = await companyService.getUserCompanies(userId);
    return companies.map(toCompanySubscription);
  },

  // Connected to endeeko `createSubscription` callable; mock-first via withBackend.
  create: (input: Omit<IndustrySubscription, 'id' | 'startDate' | 'renewalDate' | 'endDate'>) => {
    const local = (): Promise<IndustrySubscription> => {
      const start = new Date();
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + (input.billingInterval === 'yearly' ? 1 : 0));
      if (input.billingInterval === 'monthly') end.setMonth(end.getMonth() + 1);
      const sub: IndustrySubscription = {
        id: uid('sub'),
        ...input,
        startDate: start.toISOString(),
        renewalDate: end.toISOString(),
        endDate: end.toISOString(),
      };
      CURRENT = sub;
      HISTORY.unshift(sub);
      BILLING = [
        {
          id: uid('inv'),
          subscriptionId: sub.id,
          invoiceNumber: `INV-${Date.now()}`,
          amount: sub.amount,
          currency: sub.currency,
          status: sub.status === 'trialing' ? 'pending' : 'paid',
          issuedAt: start.toISOString(),
          paidAt: sub.status === 'trialing' ? undefined : start.toISOString(),
        },
        ...BILLING,
      ];
      return simulate(sub);
    };
    return withBackend<IndustrySubscription>('createSubscription', { subscription: input }, local);
  },

  // Connected to endeeko `consumeTrialTokens` callable; mock-first via withBackend.
  consumeTokens: (amount: number) => {
    const local = () => {
      if (CURRENT && CURRENT.status === 'trialing') {
        CURRENT = { ...CURRENT, trialTokens: Math.max(0, (CURRENT.trialTokens ?? 0) - amount) };
      }
      return simulate(CURRENT);
    };
    return withBackend<IndustrySubscription | null>('consumeTrialTokens', { amount }, local);
  },

  cancel: (reason?: string) => {
    const local = () => {
      if (CURRENT) CURRENT = { ...CURRENT, status: 'cancelled', cancelledAt: new Date().toISOString() };
      return simulate(CURRENT);
    };
    return withBackend<IndustrySubscription | null>('cancelBusinessSubscription', { reason }, local);
  },

  // Plan changes route through the endeeko `upgradeBusinessPlan` callable; mock-first.
  upgrade: (planId: string) => {
    const local = () => {
      if (CURRENT) CURRENT = { ...CURRENT, planId };
      return simulate(CURRENT);
    };
    return withBackend<IndustrySubscription | null>('upgradeBusinessPlan', { planId, direction: 'upgrade' }, local);
  },

  downgrade: (planId: string) => {
    const local = () => {
      if (CURRENT) CURRENT = { ...CURRENT, planId };
      return simulate(CURRENT);
    };
    return withBackend<IndustrySubscription | null>('upgradeBusinessPlan', { planId, direction: 'downgrade' }, local);
  },
};
